import { type ButtonHTMLAttributes, forwardRef, type ReactNode } from 'react';
import { haptics } from '../lib/platform';
import s from './Button.module.css';
import { Icon, type IconName } from './Icon';

type Variant = 'primary' | 'quiet' | 'line' | 'ghost' | 'danger';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: 'md' | 'sm';
  icon?: IconName;
  wide?: boolean;
  children?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'quiet',
    size = 'md',
    icon,
    wide,
    className,
    children,
    onClick,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={[s.button, s[variant], s[size], wide ? s.wide : '', className ?? ''].join(' ')}
      onClick={(e) => {
        if (variant === 'primary') void haptics.impact('light');
        onClick?.(e);
      }}
      {...rest}
    >
      {icon && <Icon name={icon} size={size === 'sm' ? 18 : 20} />}
      {children && <span>{children}</span>}
    </button>
  );
});

interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: IconName;
  label: string;
  variant?: 'plain' | 'filled' | 'sky';
  size?: number;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, label, variant = 'plain', size = 22, className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      title={label}
      className={[s.iconButton, s[`icon_${variant}`], className ?? ''].join(' ')}
      {...rest}
    >
      <Icon name={icon} size={size} />
    </button>
  );
});
